import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";

import type { ApiErrorEnvelope, RuleVersion } from "./types";

/**
 * React Query cache key for `GET /rules/versions`.
 *
 * Exposed as a stable constant so `usePublishRules` can invalidate
 * the version list after a write — publishing adds a new version at
 * the top of the list.
 */
export const LIST_RULE_VERSIONS_QUERY_KEY = [
  "LIST_WITHDRAWAL_RULE_VERSIONS",
] as const;

async function fetchRuleVersions(): Promise<readonly RuleVersion[]> {
  const instance = getPrivateInstance();
  const response = await instance.get<{ data: readonly RuleVersion[] }>(
    API_ROUTES.LIST_WITHDRAWAL_RULE_VERSIONS,
  );
  const versions = response?.data?.data;
  // Defensive: BE contract says `data` is always an array (possibly
  // empty). Anything else means the envelope changed shape — treat
  // as empty rather than crashing consumers with `.map` on undefined.
  return Array.isArray(versions) ? versions : [];
}

/**
 * Fetches the list of published rule-graph versions, ordered newest
 * first per BE contract. Exactly one entry has `is_current: true`
 * (the version returned by `useGetActiveRules`).
 *
 * Success paths:
 * - Any number of versions (including empty array when nothing has
 *   ever been published).
 *
 * Error paths (surfaced via `isError`):
 * - 403 → permission missing (`compliance.withdrawals.list_versions`).
 * - 5xx → transient, retry per caller's `QueryClient` policy.
 *
 * There is no 404 case here — the list endpoint exists as long as
 * the compliance service is up, and returns an empty array rather
 * than 404 when no versions exist.
 */
export function useListRuleVersions(): UseQueryResult<
  readonly RuleVersion[],
  AxiosError<ApiErrorEnvelope>
> {
  return useQuery<readonly RuleVersion[], AxiosError<ApiErrorEnvelope>>({
    queryKey: LIST_RULE_VERSIONS_QUERY_KEY,
    queryFn: fetchRuleVersions,
    // Version lists change only on publish/activate — the mutation
    // hooks invalidate this key, so a 60s stale window is safe.
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}
